import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import { actionOnBroken, actionOnClosed, actionOnError, actionOnMessage } from '@skynet/data-ws-mm';
import type { JsonFailure } from '@skynet/data-ws-core';
import { registerErrorMessage, isOkCommand, parseResponse } from '@skynet/data-ws-core';
import { staleCoinGeckoLinkPatch } from '@skynet/data-helpers';

import type {
  JsonAddRetainerProjectSuccess,
  JsonRetainerProjectTermsSuccess,
  RetainerTermsState,
} from './types';
import { RetainerTermsCommands } from './types';
import type { UpdateRetainerProjectPayload } from './actions';
import { addRetainerProject, updateRetainerProject } from './actions';
import { listen } from '../actions';
import { defaults } from 'es-toolkit/compat';

registerErrorMessage(RetainerTermsCommands.UpdateRetainer, {
  title: 'Update Retainer Error',
  getDescription: (json: JsonFailure) => String(json.message),
});

export const initialState: RetainerTermsState = {
  id: undefined,
  details: undefined,
  requestStatus: null,
  resUpdateRetainer: null,
  confirmedTerms: undefined,
  error: null,
  isRetainerEditing: false, // to handle loan editing mode
};

const allowedCommands = Object.values(RetainerTermsCommands);

/**
 * Ends the update (or AddProject) in flight as failed; for an update, also drops its optimistic
 * merge, putting back the details the server kept (see `confirmedTerms`).
 */
const failPendingUpdate = (state: RetainerTermsState) => {
  if (state.resUpdateRetainer === 'pending' && state.confirmedTerms) {
    state.id = state.confirmedTerms.id;
    state.details = state.confirmedTerms.details;
  }
  state.confirmedTerms = undefined;
  state.resUpdateRetainer = 'error';
};

const projectTermsSlice = createSlice({
  name: 'terms',
  initialState,
  reducers: {
    toggleEditing(state, action: { payload: { value?: boolean } }) {
      console.debug('toggleRetainerEdit action:', action);
      state.isRetainerEditing =
        action.payload.value != null ? action.payload.value : !state.isRetainerEditing;
    },
    resetUpdateRetainer(state) {
      state.resUpdateRetainer = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateRetainerProject.pending, (state, action) => {
      console.log('retainers/updateRetainerProject:', action);
      // Like `resUpdateRetainer`, this models one update at a time: a second one sent before
      // the first is acked keeps the first one's baseline, the last state the server confirmed.
      if (state.resUpdateRetainer !== 'pending') {
        state.confirmedTerms = { id: state.id, details: state.details };
      }
      state.resUpdateRetainer = 'pending';
      state.error = null;
    });

    builder.addCase(updateRetainerProject.rejected, (state, action) => {
      console.log('retainers/updateRetainerProject.rejected:', action);
      state.requestStatus = 'error';
    });

    builder.addCase(updateRetainerProject.fulfilled, (state, action) => {
      console.debug('retainers/update.fulfilled', action.payload);
      const updatedData: UpdateRetainerProjectPayload = action.payload;
      if (!updatedData || !state.details) {
        return;
      }
      // The patch is computed before `defaults` runs: it fills `updatedData` in place, after
      // which an untouched project would be indistinguishable from an unchanged one anyway.
      const staleLinkPatch = staleCoinGeckoLinkPatch(state.details, updatedData);

      state.details = {
        ...defaults(updatedData, state.details),
        tokens: updatedData.tokens ?? state.details.tokens ?? [],
        ...staleLinkPatch,
      };
      state.error = null;
    });

    builder.addCase(addRetainerProject, (state, action) => {
      console.log('retainers/updateRetainerProject:', action);
      state.resUpdateRetainer = 'pending';
      state.error = null;
    });

    builder.addCase(listen, (state, action) => {
      const { projectID } = action.payload;
      const { isInitCall } = action.meta;
      if (isInitCall) {
        state.requestStatus = 'pending';
      }
      if (!projectID) {
        state.details = undefined;
      }
      // Another project (or none): never restore the previous one's details over it.
      state.confirmedTerms = undefined;
    });

    builder.addCase(actionOnMessage, (state, action) => {
      const { json, skipProcessing, error, errorCommand } = parseResponse(
        action.payload,
        allowedCommands,
      );

      if (skipProcessing || !json) return;

      console.debug(`retainers/processing ${actionOnMessage.toString()}`, action, allowedCommands);

      const { command, okCommand } = json;

      if (error) {
        state.error = error;
        switch (errorCommand) {
          case RetainerTermsCommands.AddProject:
          case RetainerTermsCommands.UpdateRetainer:
            failPendingUpdate(state);
            break;
          case undefined:
          default:
        }
        return;
      }

      if (command === RetainerTermsCommands.Terms) {
        const { details, id }: JsonRetainerProjectTermsSuccess = json;
        state.id = id;
        state.details = {
          ...details,
          tokens: details.tokens ?? [],
        };
        state.requestStatus = 'success';
        // The server's own state: nothing older is left to fall back on.
        state.confirmedTerms = undefined;
      }

      if (isOkCommand(command) && okCommand === RetainerTermsCommands.UpdateRetainer) {
        const { result: projectID }: JsonAddRetainerProjectSuccess = json;
        state.resUpdateRetainer = 'success';
        state.confirmedTerms = undefined;
        state.error = null;
        console.debug('UpdateRetainer RetainerID:', projectID, json);
        // const updatedData: UpdateRetainerProjectPayload = action.payload;
        // if (!updatedData) {
        //   return;
        // }
        // state.details = merge({}, state.details, updatedData);
      }

      if (isOkCommand(command) && okCommand === RetainerTermsCommands.AddProject) {
        state.resUpdateRetainer = 'success';
      }
    });

    // The socket carrying the ack went away, so the ack can no longer arrive: settle the
    // update instead of leaving the form's switches (or the new-project modal) locked until the page is reopened.
    // These are every terminal socket event the middleware dispatches (there is no `::CLOSE`).
    // The command may still have reached the server, which re-sends Terms after a reconnect.
    builder.addMatcher(isAnyOf(actionOnClosed, actionOnBroken, actionOnError), (state) => {
      if (state.resUpdateRetainer === 'pending') {
        failPendingUpdate(state);
      }
    });
  },
});

export const retainerTermsActions = {
  ...projectTermsSlice.actions,
  updateRetainerProject,
  addRetainerProject,
};

export default projectTermsSlice.reducer;
